<template>
    <div class="bg-slate-100 min-h-screen pt-10 pb-10">
        <div class="max-w-3xl mx-auto">

            <!-- LOADING INITIAL -->
            <div v-if="loadingGetData" class="flex items-center justify-center h-64">
                <i class="fas fa-spinner fa-spin text-3xl text-gray-400"></i>
            </div>

            <div v-else-if="chantier">

                <!-- ENTÊTE SOCIÉTÉ -->
                <div class="bg-white shadow rounded-lg p-6 mb-4 flex items-center gap-4">
                    <img
                        v-if="chantier.societe.logo"
                        :src="chantier.societe.logo"
                        class="h-14 rounded object-contain"
                    />
                    <div>
                        <h1 class="text-xl font-bold text-gray-800">{{ chantier.societe.nom }}</h1>
                        <div class="text-sm text-gray-500">
                            {{ chantier.societe.adresse }}, {{ chantier.societe.code_postal }} {{ chantier.societe.ville }}
                        </div>
                        <div class="text-sm text-gray-500">{{ chantier.societe.tel }}</div>
                    </div>
                </div>

                <!-- RÉSUMÉ CHANTIER -->
                <div class="bg-white shadow rounded-lg p-6 mb-4">
                    <h2 class="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <i class="fas fa-hard-hat text-blue-600"></i>
                        Récapitulatif du chantier
                    </h2>
                    <div class="grid grid-cols-2 gap-3 text-sm mb-4">
                        <div>
                            <span class="text-gray-500">Client</span>
                            <div class="font-medium text-gray-800 mt-0.5">
                                {{ chantier.client.prenom }} {{ chantier.client.nom }}
                            </div>
                        </div>
                        <div>
                            <span class="text-gray-500">Contact</span>
                            <div class="font-medium text-gray-800 mt-0.5">{{ chantier.client.telephone }}</div>
                        </div>
                        <div class="col-span-2">
                            <span class="text-gray-500">Adresse d'intervention</span>
                            <div class="font-medium text-gray-800 mt-0.5">
                                {{ chantier.adresse.adresse1 }}, {{ chantier.adresse.code_postal }} {{ chantier.adresse.ville }}
                            </div>
                            <div v-if="chantier.adresse.acces" class="text-xs text-gray-400 mt-0.5">
                                Code d'accès : {{ chantier.adresse.acces }}
                            </div>
                            <div v-if="chantier.adresse.notes" class="text-xs italic text-gray-400 mt-0.5">
                                {{ chantier.adresse.notes }}
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                        <span class="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1 rounded-full">
                            <i class="fas fa-tools"></i>
                            {{ chantier.interventions.length }} intervention{{ chantier.interventions.length > 1 ? 's' : '' }}
                        </span>
                        <span class="inline-flex items-center gap-1.5 bg-violet-50 text-violet-700 text-xs font-medium px-3 py-1 rounded-full">
                            <i class="fas fa-file-alt"></i>
                            {{ chantier.documents.length }} document{{ chantier.documents.length > 1 ? 's' : '' }}
                        </span>
                        <span v-if="chantier.terminer" class="inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-xs font-medium px-3 py-1 rounded-full">
                            <i class="fas fa-check-circle"></i>
                            Chantier terminé
                        </span>
                        <span v-else class="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1 rounded-full">
                            <i class="fas fa-clock"></i>
                            En cours
                        </span>
                    </div>
                </div>

                <!-- INTERVENTIONS -->
                <div class="bg-white shadow rounded-lg p-6 mb-4">
                    <h2 class="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <i class="fas fa-wrench text-blue-600"></i>
                        Interventions réalisées
                    </h2>
                    <div class="space-y-3">
                        <div
                            v-for="(intervention, index) in chantier.interventions"
                            :key="intervention.id"
                            class="border border-gray-100 rounded-lg p-4 bg-gray-50"
                        >
                            <div class="flex items-start justify-between gap-2 mb-2">
                                <div class="flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                                        {{ index + 1 }}
                                    </span>
                                    <span class="text-sm font-medium text-gray-800">{{ intervention.reference }}</span>
                                </div>
                                <span class="text-xs text-gray-400 whitespace-nowrap">
                                    {{ formatDate(intervention.date_intervention) }}
                                </span>
                            </div>
                            <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500 pl-8">
                                <div>
                                    <i class="fas fa-clock mr-1"></i>
                                    {{ intervention.heure_debut }} → {{ intervention.heure_fin }}
                                </div>
                                <div>
                                    <i class="fas fa-hourglass-half mr-1"></i>
                                    {{ intervention.duree_estimee }} min estimées
                                </div>
                                <div v-if="intervention.heure_debut_tech && intervention.heure_fin_tech">
                                    <i class="fas fa-user-clock mr-1"></i>
                                    Réel : {{ intervention.heure_debut_tech }} → {{ intervention.heure_fin_tech }}
                                </div>
                                <div>
                                    <i class="fas fa-tag mr-1"></i>
                                    {{ typeLabels[intervention.type_id] || 'Type ' + intervention.type_id }}
                                </div>
                            </div>
                            <div v-if="getDocsForIntervention(intervention.id).length" class="mt-3 pl-8 flex flex-wrap gap-2">
                                <a
                                    v-for="doc in getDocsForIntervention(intervention.id)"
                                    :key="doc.id"
                                    :href="doc.url"
                                    target="_blank"
                                    class="inline-flex items-center gap-1.5 text-xs bg-white border border-gray-200 text-gray-600 hover:text-blue-600 hover:border-blue-300 px-2.5 py-1 rounded-full transition-colors"
                                >
                                    <i :class="docIcon(doc.type)"></i>
                                    {{ docTypeLabels[doc.type] || doc.type }}
                                    <i class="fas fa-external-link-alt text-gray-300 text-xs"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- TOUS LES DOCUMENTS -->
                <div class="bg-white shadow rounded-lg p-6 mb-4">
                    <h2 class="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <i class="fas fa-folder-open text-blue-600"></i>
                        Tous les documents
                    </h2>
                    <div v-if="chantier.documents.length" class="space-y-2">
                        <a
                            v-for="doc in chantier.documents"
                            :key="doc.id"
                            :href="doc.url"
                            target="_blank"
                            class="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 hover:border-blue-200 transition-all group"
                        >
                            <div class="flex items-center gap-3">
                                <span class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                    <i :class="[docIcon(doc.type), 'text-blue-600 text-sm']"></i>
                                </span>
                                <div>
                                    <div class="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                                        {{ docTypeLabels[doc.type] || doc.type }}
                                    </div>
                                    <div v-if="doc.intervention_id" class="text-xs text-gray-400">
                                        Intervention #{{ getInterventionIndex(doc.intervention_id) + 1 }} · {{ getInterventionRef(doc.intervention_id) }}
                                    </div>
                                    <div v-else class="text-xs text-gray-400">Document de chantier</div>
                                </div>
                            </div>
                            <i class="fas fa-external-link-alt text-gray-300 group-hover:text-blue-400 transition-colors text-xs"></i>
                        </a>
                    </div>
                    <div v-else class="text-sm text-gray-400 italic">Aucun document disponible.</div>
                </div>

                <!-- SIGNATURE -->
                <div class="bg-white shadow rounded-lg p-6 mb-4">
                    <h2 class="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <i class="fas fa-pen-nib text-blue-600"></i>
                        Signature du client
                    </h2>

                    <!-- Déjà signé -->
                    <template v-if="isAlreadySigned">
                        <div class="bg-green-50 border border-green-200 rounded-lg p-5">
                            <div class="flex items-start gap-3">
                                <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                    <i class="fas fa-check text-green-600"></i>
                                </div>
                                <div>
                                    <p class="font-semibold text-green-700">Documents signés</p>
                                    <p class="text-sm text-green-600 mt-0.5 font-light">
                                        Merci pour votre confiance. Nous restons à votre disposition pour toute question.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- À signer -->
                    <template v-else>
                        <p class="text-sm text-gray-500 mb-4">
                            En signant ci-dessous, vous confirmez la bonne réalisation de l'ensemble des interventions
                            et l'exactitude des informations présentées.
                        </p>
                        <input-signature
                            v-model="signature"
                            :keyName="'chantier-' + $route.params.token"
                            btn-label="Valider et signer"
                            btn-class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            @input="openSignModal"
                        />
                        <p class="text-xs text-gray-400 mt-3">
                            Cette signature électronique a valeur contractuelle.
                        </p>
                    </template>
                </div>

                <!-- PORTAIL CLIENT -->
                <div v-if="chantier.societe.has_portail && isAlreadySigned" class="bg-white shadow rounded-lg p-6 mb-4">
                    <div class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
                        <div class="flex items-start gap-4">
                            <div class="bg-blue-600 text-white rounded-full p-3 flex-shrink-0">
                                <i class="fas fa-desktop"></i>
                            </div>
                            <div class="flex-1">
                                <h3 class="font-semibold text-gray-800 mb-1">Votre portail client</h3>
                                <p class="text-sm text-gray-600 mb-4">
                                    Consultez vos interventions, téléchargez vos documents et gérez vos informations depuis votre espace personnel.
                                </p>
                                <a
                                    :href="'/c/' + chantier.client.token"
                                    target="_blank"
                                    class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <i class="fas fa-arrow-right"></i>
                                    Accéder à mon portail
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <!-- Erreur -->
            <div v-else class="bg-white shadow rounded-lg p-10 text-center text-gray-400">
                <i class="fas fa-exclamation-triangle text-3xl mb-3"></i>
                <p>Impossible de charger les données du chantier.</p>
            </div>
        </div>

        <!-- ===================== MODALE DE SIGNATURE ===================== -->
        <transition name="modal-fade">
            <div
                v-if="showSignModal"
                class="fixed inset-0 z-50 flex items-center justify-center"
                style="background: rgba(0,0,0,0.5)"
            >
                <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

                    <!-- En cours de signature -->
                    <template v-if="!signatureComplete">
                        <div class="px-6 pt-6 pb-4 border-b border-gray-100">
                            <h3 class="text-base font-semibold text-gray-800">Signature des documents en cours</h3>
                            <p class="text-sm text-gray-500 mt-0.5">Merci de patienter, ne fermez pas cette page.</p>
                        </div>

                        <div class="px-6 py-4 space-y-2 max-h-72 overflow-y-auto">
                            <div
                                v-for="(item, index) in signQueue"
                                :key="item.doc.id"
                                class="flex items-center gap-3 p-3 rounded-lg transition-all duration-300"
                                :class="{
                                    'bg-blue-50'  : item.status === 'signing',
                                    'bg-green-50' : item.status === 'done',
                                    'bg-gray-50'  : item.status === 'pending',
                                }"
                            >
                                <!-- Icône statut -->
                                <div
                                    class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300"
                                    :class="{
                                        'bg-blue-100'  : item.status === 'signing',
                                        'bg-green-100' : item.status === 'done',
                                        'bg-gray-100'  : item.status === 'pending',
                                    }"
                                >
                                    <i v-if="item.status === 'pending'" class="fas fa-clock text-gray-400 text-xs"></i>
                                    <i v-else-if="item.status === 'signing'" class="fas fa-spinner fa-spin text-blue-500 text-xs"></i>
                                    <i v-else class="fas fa-check text-green-500 text-xs"></i>
                                </div>

                                <!-- Infos document -->
                                <div class="flex-1 min-w-0">
                                    <div
                                        class="text-sm font-medium truncate transition-colors duration-300"
                                        :class="{
                                            'text-blue-700'  : item.status === 'signing',
                                            'text-green-700' : item.status === 'done',
                                            'text-gray-400'  : item.status === 'pending',
                                        }"
                                    >
                                        {{ docTypeLabels[item.doc.type] || item.doc.type }}
                                    </div>
                                    <div class="text-xs text-gray-400">
                                        <span v-if="item.doc.intervention_id">
                                            Intervention #{{ getInterventionIndex(item.doc.intervention_id) + 1 }}
                                        </span>
                                        <span v-else>Document de chantier</span>
                                    </div>
                                </div>

                                <!-- Label statut -->
                                <span v-if="item.status === 'signing'" class="text-xs text-blue-500 font-medium whitespace-nowrap">En cours…</span>
                                <span v-else-if="item.status === 'done'" class="text-xs text-green-600 font-medium whitespace-nowrap">Signé</span>
                                <span v-else class="text-xs text-gray-300 whitespace-nowrap">En attente</span>
                            </div>

                            <!-- Envoie de mail -->
                            <div
                                v-if="loadingSendMail"
                                class="flex items-center gap-3 p-3 rounded-lg transition-all duration-300 bg-yellow-50 mt-3"
                            >
                                <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-yellow-100">
                                    <i class="fas fa-envelope text-yellow-600 text-xs"></i>
                                </div>

                                <div class="flex-1 min-w-0">
                                    <div class="text-sm font-medium text-yellow-700">
                                        Envoi des documents par email
                                    </div>
                                    <div class="text-xs text-gray-400">
                                        Vous allez recevoir un email contenant tous les documents signés
                                    </div>
                                </div>

                                <span class="text-xs text-yellow-600 font-medium whitespace-nowrap">En cours…</span>
                            </div>
                        </div>

                        <!-- Barre de progression -->
                        <div class="px-6 pb-6 pt-2">
                            <div class="flex justify-between text-xs text-gray-400 mb-1.5">
                                <span>Progression</span>
                                <span>{{ signedCount }} / {{ signQueue.length }}</span>
                            </div>
                            <div class="w-full bg-gray-100 rounded-full h-1.5">
                                <div
                                    class="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                                    :style="{ width: progressPercent + '%' }"
                                ></div>
                            </div>
                        </div>
                    </template>

                    <!-- Signature terminée -->
                    <template v-else>
                        <div class="px-6 py-10 flex flex-col items-center text-center">
                            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
                                <i class="fas fa-check-circle text-green-500 text-3xl"></i>
                            </div>
                            <h3 class="text-lg font-semibold text-gray-800 mb-2">Tout est signé !</h3>
                            <p class="text-sm text-gray-500 leading-relaxed mb-6">
                                Merci pour votre collaboration.
                                L'ensemble de vos documents ont bien été signés et enregistrés.
                                Nous vous souhaitons une excellente {{ new Date().getHours() >= 18 ? 'soirée' : 'journée' }}.
                            </p>
                            <button
                                @click="closeSignModal"
                                class="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                            >
                                Fermer
                            </button>
                        </div>
                    </template>

                </div>
            </div>
        </transition>

    </div>
</template>

<script>
import DocType from './../../config/docType.js';

export default {
    name: 'SignChantier',

    data() {
        return {
            chantier: null,
            docTypeLabels: DocType,

            typeLabels: {
                507: 'Entretien',
                512: 'Dépannage',
            },

            signature: null,
            loadingGetData: false,

            // Modale
            showSignModal: false,
            signatureComplete: false,
            signQueue: [], // [{ doc, status: 'pending' | 'signing' | 'done' }]

            loadingSendMail: false,
        }
    },

    computed: {
        isAlreadySigned() {
            if (!this.chantier) return false;
            return this.chantier.interventions.some(i => i.signature_client !== null);
        },

        signedCount() {
            return this.signQueue.filter(i => i.status === 'done').length;
        },

        progressPercent() {
            if (!this.signQueue.length) return 0;
            return Math.round((this.signedCount / this.signQueue.length) * 100);
        },
    },

    methods: {
        async fetchChantierData() {
            this.loadingGetData = true;
            try {
                const response = await this.$request.post('get-intervention-token', {
                    intervention_token: this.$route.params.token,
                });
                this.chantier = response;

                const logoUrl = this.chantier.societe?.logo;
                if (logoUrl) {
                    const oldLink = document.querySelector("link[rel~='icon']");
                    if (oldLink) oldLink.remove();
                    const link = document.createElement('link');
                    link.rel = 'icon';
                    link.type = 'image/png';
                    link.href = logoUrl;
                    document.head.appendChild(link);
                }

                const nomSociete = this.chantier.societe?.nom || '';
                document.title = `Signature du chantier${nomSociete ? ' - ' + nomSociete : ''}`;
            } catch (e) {
                this.$toast.error('Impossible de charger le chantier.');
            } finally {
                this.loadingGetData = false;
            }
        },

        formatDate(date) {
            return new Date(date).toLocaleDateString('fr-FR');
        },

        getDocsForIntervention(interventionId) {
            if (!this.chantier) return [];
            return this.chantier.documents.filter(d => d.intervention_id === interventionId);
        },

        getInterventionIndex(interventionId) {
            return this.chantier.interventions.findIndex(i => i.id === interventionId);
        },

        getInterventionRef(interventionId) {
            const i = this.chantier.interventions.find(i => i.id === interventionId);
            return i ? i.reference : '';
        },

        docIcon(type) {
            const icons = {
                bon_intervention:      'fas fa-clipboard-check',
                contrat_entretien:     'fas fa-file-contract',
                attestation_entretien: 'fas fa-certificate',
                rapport_chantier:      'fas fa-hard-hat',
                facture:               'fas fa-file-invoice-dollar',
                devis:                 'fas fa-file-signature',
            };
            return icons[type] || 'fas fa-file-alt';
        },

        openSignModal() {
            if (!this.signature) {
                return this.$toast.error('Merci de signer avant de valider.');
            }

            // Initialiser la file avec tous les documents
            this.signQueue = this.chantier.documents.map(doc => ({
                doc,
                status: 'pending',
            }));

            this.signatureComplete = false;
            this.showSignModal = true;

            this.$nextTick(() => this.runSignQueue());
        },

        async runSignQueue() {
            for (let i = 0; i < this.signQueue.length; i++) {
                // Passer en "signing"
                this.$set(this.signQueue, i, { ...this.signQueue[i], status: 'signing' });

                try {
                    await this.$request.post(`/doc-intervention/${this.signQueue[i].doc.id}/sign`, {
                        url_signature: this.signature,
                    });
                } catch (e) {
                    console.error(`Erreur signature doc #${this.signQueue[i].doc.id}`, e);
                }

                // Passer en "done"
                this.$set(this.signQueue, i, { ...this.signQueue[i], status: 'done' });
            }

            this.loadingSendMail = true;
            await this.$request.post('/chantier/send-documents', {
                chantier_id: this.chantier.id,
            })
            this.loadingSendMail = false;

            // Petit délai avant affichage du succès pour que le dernier check soit visible
            await this.sleep(600);
            this.signatureComplete = true;

            // Recharger les données en arrière-plan
            this.fetchChantierData();
        },

        closeSignModal() {
            this.showSignModal = false;
            this.signatureComplete = false;
            this.signQueue = [];
        },

        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
    },

    mounted() {
        this.fetchChantierData();
    },
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.25s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
    opacity: 0;
}
</style>